// PWA：注册 service worker + 自动更新到最新版本
// 「每次打开都是最新版」靠三层：
// 1. 启动时先比对 /version.json 的构建号，跟当前代码不一样就立刻刷新（还没渲染，用户看不到旧版）
// 2. 从后台切回来（手机上「打开」PWA 多半是恢复而不是重新加载）再比对一次，不一样就刷新
// 3. 开着用的时候每 5 分钟比对一次，有新版只提示「有新版本」，不打断正在填的表单
import { useEffect, useState } from "react";

const CHECK_MS = 5 * 60_000;
/** 切回前台时，离开超过这么久才检查（来回切换不反复请求） */
const RESUME_MS = 10_000;
/** 同一个新构建号最多自动刷新一次，防止服务器半新半旧时来回刷新 */
const RELOADED_KEY = "csp-pwa-reloaded";

type Listener = (build: string | null) => void;
const listeners = new Set<Listener>();
let pending: string | null = null;

function notify(build: string | null) {
  pending = build;
  for (const l of listeners) l(build);
}

/** 服务器上的构建号；拿不到（断网、超时）返回 null */
export async function serverBuild(timeoutMs = 2500): Promise<string | null> {
  const ctl = new AbortController();
  const t = window.setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const r = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store", signal: ctl.signal });
    if (!r.ok) return null;
    const j = (await r.json()) as { build?: unknown };
    return typeof j.build === "string" ? j.build : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(t);
  }
}

/** 自动刷新到 build；同一个 build 已经刷过一次就不再刷（改为提示），返回是否刷新了 */
export function reloadOnce(build: string, storage: Pick<Storage, "getItem" | "setItem"> = sessionStorage, reload = () => window.location.reload()): boolean {
  try {
    if (storage.getItem(RELOADED_KEY) === build) return false;
    storage.setItem(RELOADED_KEY, build);
  } catch {
    /* 隐私模式拿不到 sessionStorage：照样刷新 */
  }
  reload();
  return true;
}

async function check(auto: boolean): Promise<boolean> {
  const b = await serverBuild();
  if (!b || b === __CSP_BUILD__) return false;
  if (auto && reloadOnce(b)) return true;
  notify(b);
  return false;
}

/** 启动前调用：有新版本就刷新并返回 true（调用方不要再渲染） */
export async function bootCheck(): Promise<boolean> {
  if (!import.meta.env.PROD) return false;
  return check(true);
}

/** 渲染后调用：注册 service worker、切回前台检查、定时检查 */
export function startPwa(): void {
  if (!import.meta.env.PROD) return;
  const loadedAt = Date.now();
  if ("serviceWorker" in navigator) {
    const hadController = !!navigator.serviceWorker.controller;
    // 新 worker 接管 = 新版本已装好：刚打开的页面直接刷新，开着用了一阵的只提示
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!hadController) return;
      if (Date.now() - loadedAt < 15_000 || document.visibilityState === "hidden") reloadOnce(`sw-${loadedAt}`);
      else notify(pending ?? "new");
    });
    navigator.serviceWorker
      .register("/sw.js", { updateViaCache: "none" })
      .then((reg) => {
        void reg.update().catch(() => {});
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") void reg.update().catch(() => {});
        });
      })
      .catch((e) => console.warn("[pwa] service worker 注册失败", e));
  }
  let hiddenAt = 0;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") hiddenAt = Date.now();
    else if (hiddenAt && Date.now() - hiddenAt > RESUME_MS) void check(true);
  });
  window.setInterval(() => {
    if (document.visibilityState === "visible") void check(false);
  }, CHECK_MS);
}

/** 有新版本没自动刷新时返回 true，用来显示「有新版本」条 */
export function useUpdateAvailable(): boolean {
  const [b, setB] = useState<string | null>(pending);
  useEffect(() => {
    listeners.add(setB);
    return () => {
      listeners.delete(setB);
    };
  }, []);
  return !!b;
}

/** 用户点「更新」：直接刷新（导航请求走网络，拿到的就是最新版） */
export function applyUpdate(): void {
  window.location.reload();
}
